import { useState, useEffect } from 'react';
import styles from './FilterDrawer.module.css';

interface FilterOptions {
  category?: string;
  subcategory?: string;
  state?: string;
  gradeLevel?: string;
  source?: string;
  difficulty?: string;
}

interface CategoryData {
  name: string;
  count: number;
  subcategories: Array<{ name: string; count: number }>;
}

interface FilterDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  filters: FilterOptions;
  onFiltersChange: (filters: FilterOptions) => void;
}

export function FilterDrawer({ isOpen, onClose, filters, onFiltersChange }: FilterDrawerProps) {
  const [categories, setCategories] = useState<CategoryData[]>([]);
  const [sources, setSources] = useState<Array<{ source: string; count: number }>>([]);
  const [loading, setLoading] = useState(true);

  // Fetch filter options on mount
  useEffect(() => {
    const fetchFilterData = async () => {
      try {
        const [catRes, srcRes] = await Promise.all([
          fetch('https://balanced-arena.davidoberger.workers.dev/api/questions/meta/categories/tree'),
          fetch('https://balanced-arena.davidoberger.workers.dev/api/questions/meta/sources'),
        ]);

        const catData = await catRes.json();
        const srcData = await srcRes.json();

        setCategories(catData);
        setSources(srcData.sources || []);
      } catch (err) {
        console.error('Failed to fetch filter data:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchFilterData();
  }, []);

  const handleFilterChange = (key: keyof FilterOptions, value: string) => {
    const newFilters = { ...filters };
    if (value === '') {
      delete newFilters[key];
    } else {
      newFilters[key] = value;
    }
    // Reset subcategory when category changes
    if (key === 'category') {
      delete newFilters.subcategory;
    }
    onFiltersChange(newFilters);
  };

  const clearAllFilters = () => {
    onFiltersChange({});
  };

  const activeFilterCount = Object.keys(filters).length;

  // Get subcategories for selected category
  const selectedCategoryData = categories.find(c => c.name === filters.category);
  const subcategories = selectedCategoryData?.subcategories || [];

  // Extract unique states and grades from subcategories
  const states = ['Nevada', 'Common Core']; // Can be expanded
  const gradeLevels = ['K', '1', '2', '3', '4', '5', '6', '7', '8', '9-12'];
  const difficulties = [
    { value: 'easy', label: 'Easy' },
    { value: 'medium', label: 'Medium' },
    { value: 'hard', label: 'Hard' },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        className={`${styles.backdrop} ${isOpen ? styles.open : ''}`}
        onClick={onClose}
      />

      {/* Drawer */}
      <div className={`${styles.drawer} ${isOpen ? styles.open : ''}`}>
        <div className={styles.header}>
          <h2 className={styles.title}>
            Filters
            {activeFilterCount > 0 && (
              <span className={styles.filterCount}>{activeFilterCount}</span>
            )}
          </h2>
          <button className={styles.closeButton} onClick={onClose}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className={styles.content}>
          {loading ? (
            <div className={styles.loading}>Loading filters...</div>
          ) : (
            <>
              {/* Category Filter */}
              <div className={styles.filterSection}>
                <label className={styles.filterLabel}>Category</label>
                <select
                  value={filters.category || ''}
                  onChange={(e) => handleFilterChange('category', e.target.value)}
                  className={styles.select}
                >
                  <option value="">All Categories</option>
                  {categories.map((cat) => (
                    <option key={cat.name} value={cat.name}>
                      {cat.name} ({cat.count})
                    </option>
                  ))}
                </select>
              </div>

              {/* Subcategory Filter (only show if category selected) */}
              {subcategories.length > 0 && (
                <div className={styles.filterSection}>
                  <label className={styles.filterLabel}>Subcategory</label>
                  <select
                    value={filters.subcategory || ''}
                    onChange={(e) => handleFilterChange('subcategory', e.target.value)}
                    className={styles.select}
                  >
                    <option value="">All Subcategories</option>
                    {subcategories.map((sub) => (
                      <option key={sub.name} value={sub.name}>
                        {sub.name} ({sub.count})
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {/* State Filter */}
              <div className={styles.filterSection}>
                <label className={styles.filterLabel}>State/Standard</label>
                <select
                  value={filters.state || ''}
                  onChange={(e) => handleFilterChange('state', e.target.value)}
                  className={styles.select}
                >
                  <option value="">All States</option>
                  {states.map((state) => (
                    <option key={state} value={state}>
                      {state}
                    </option>
                  ))}
                </select>
              </div>

              {/* Grade Level Filter */}
              <div className={styles.filterSection}>
                <label className={styles.filterLabel}>Grade Level</label>
                <select
                  value={filters.gradeLevel || ''}
                  onChange={(e) => handleFilterChange('gradeLevel', e.target.value)}
                  className={styles.select}
                >
                  <option value="">All Grades</option>
                  {gradeLevels.map((grade) => (
                    <option key={grade} value={grade}>
                      Grade {grade}
                    </option>
                  ))}
                </select>
              </div>

              {/* Difficulty Filter */}
              <div className={styles.filterSection}>
                <label className={styles.filterLabel}>Difficulty</label>
                <select
                  value={filters.difficulty || ''}
                  onChange={(e) => handleFilterChange('difficulty', e.target.value)}
                  className={styles.select}
                >
                  <option value="">All Difficulties</option>
                  {difficulties.map((diff) => (
                    <option key={diff.value} value={diff.value}>
                      {diff.label}
                    </option>
                  ))}
                </select>
              </div>

              {/* Source Filter */}
              <div className={styles.filterSection}>
                <label className={styles.filterLabel}>Source</label>
                <select
                  value={filters.source || ''}
                  onChange={(e) => handleFilterChange('source', e.target.value)}
                  className={styles.select}
                >
                  <option value="">All Sources</option>
                  {sources.map((src) => (
                    <option key={src.source} value={src.source}>
                      {src.source} ({src.count})
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}
        </div>

        <div className={styles.footer}>
          <button
            className={styles.clearButton}
            onClick={clearAllFilters}
            disabled={activeFilterCount === 0}
          >
            Clear All
          </button>
          <button className={styles.applyButton} onClick={onClose}>
            Apply Filters
          </button>
        </div>
      </div>
    </>
  );
}
